/**
 * JSON Tree Visualizer Tool
 * 
 * Renders JSON, YAML, and TOML as an interactive, collapsible tree view.
 */

import React from 'react';
import { parse as parseTOML } from 'smol-toml';
import YAML from 'yaml';

import { InlineEditor } from '@/components/visualizers/InlineEditor';
import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

import { DEFAULT_SEARCH_MODE,JsonTreeView } from './JsonTreeView';

/** Attempt to parse input as JSON, YAML, or TOML. */
function parseStructuredInput(input: string): unknown {
  // Try JSON first (most common)
  try {
    return parseJsonLenient(input);
  } catch {
    // not JSON
  }

  // Try TOML (check before YAML since most YAML parsers accept TOML-like strings)
  try {
    const result = parseTOML(input);
    if (result && typeof result === 'object') return result;
  } catch {
    // not TOML
  }

  // Try YAML
  try {
    const result = YAML.parse(input);
    if (result !== undefined && result !== null) return result;
  } catch {
    // not YAML
  }

  throw ToolError.validation('Unable to parse input as JSON, YAML, or TOML');
}

/**
 * JSON Tree Visualizer - renders JSON as collapsible tree.
 */
class JsonTreeVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'json-tree-visualizer';
  readonly name = 'Tree View';
  readonly description = 'Display JSON, YAML, or TOML as an interactive tree with expand/collapse';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = true;
  readonly keywords = ['json', 'yaml', 'toml', 'tree', 'view', 'visualize', 'expand', 'collapse', 'visualizer'];
  
  readonly supportedInputTypes: ISemanticType[] = [
    SEMANTIC_TYPES.JSON,
    SEMANTIC_TYPES.YAML,
    SEMANTIC_TYPES.TOML,
  ];
  
  readonly outputType = SEMANTIC_TYPES.JSON;
  
  supportsInput(): boolean {
    return true;
  }
  
  readonly attributes = [
    {
      name: 'initialExpanded',
      type: 'boolean' as const,
      label: 'Initially Expanded',
      defaultValue: true,
      description: 'Whether nodes are expanded by default',
    },
    {
      name: 'maxDepth',
      type: 'number' as const,
      label: 'Max Depth',
      defaultValue: 10,
      description: 'Maximum nesting depth to display',
      validation: { min: 1, max: 50 },
    },
    {
      name: 'showTypes',
      type: 'boolean' as const,
      label: 'Show Types',
      defaultValue: true,
      description: 'Display data type indicators',
    },
    {
      name: 'zeroIndexed',
      type: 'boolean' as const,
      label: 'Zero-based Indexing',
      defaultValue: true,
      description: 'Use 0-based indexing (0, 1, 2...) instead of 1-based indexing',
    },
    {
      name: 'search',
      type: 'string' as const,
      label: 'Search',
      defaultValue: '',
      description: 'Search term used by the visualizer',
    },
    {
      name: 'searchMode',
      type: 'select' as const,
      label: 'Search Mode',
      defaultValue: DEFAULT_SEARCH_MODE,
      options: ['filter', 'highlight'],
      description: 'filter hides non-matching branches; highlight keeps everything visible and highlights matches',
    },
    {
      name: 'chunkSize',
      type: 'number' as const,
      label: 'Chunk Size (Advanced)',
      defaultValue: 100,
      description: 'Number of children to render before showing "Show more" button. Lower numbers improve initial responsiveness.',
      validation: { min: 10, max: 1000 },
    },
    {
      name: 'maxTraversalNodes',
      type: 'number' as const,
      label: 'Max Search Nodes (Advanced)',
      defaultValue: 5000,
      description: 'Maximum number of nodes to search/visit during scanning. Protects against infinite loops or extreme memory usage.',
      validation: { min: 100, max: 50000 },
    },
    {
      name: 'editStyle',
      type: 'select' as const,
      label: 'Edit Style',
      defaultValue: 'inline',
      options: ['inline', 'raw'],
      description: 'inline: click values to edit in tree; raw: edit JSON text directly',
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, editMode, onEdit } = context;
    const editStyle = (config.editStyle as string) ?? 'inline';
    
    // Raw JSON edit mode: show InlineEditor instead of tree
    if (editMode && editStyle === 'raw' && onEdit) {
      return <InlineEditor input={input} onEdit={onEdit} />;
    }

    // Empty input: start with empty object
    const effectiveInput = input.trim() || '{}';
    
    // Memoize the parse result to prevent stable inputs from causing new object references.
    const parsedData = React.useMemo(() => {
      try {
        return parseStructuredInput(effectiveInput);
      } catch (e) {
        return { __error: e instanceof Error ? e.message : 'Unable to parse input' };
      }
    }, [effectiveInput]);

    // Note: We intentionally do NOT propagate search/filter state changes (search, searchMode, showTypes)
    // back to parent config. These are managed as internal uncontrolled state by JsonTreeView.
    // Propagating them UP creates a render cycle: JsonTreeView updates → parent re-renders → 
    // JsonTreeView re-renders → TreeNode re-renders → React Error #300.
    // Initial values from config are still respected, but changes stay local to the tree.

    // Handle inline value edits: clone data, set value at path, re-serialize
    const handleValueEdit = React.useCallback((path: string, newValue: unknown) => {
      if (!onEdit) return;
      try {
        const clone = parseJsonLenient(effectiveInput);
        const segments = path.split('.').slice(1); // drop "root"
        if (segments.length === 0) {
          // Editing the root value itself (primitive root)
          onEdit(JSON.stringify(newValue, null, 2));
          return;
        }
        let target: unknown = clone;
        for (let i = 0; i < segments.length - 1; i++) {
          const seg = segments[i];
          if (Array.isArray(target)) {
            target = (target as unknown[])[Number(seg)];
          } else if (target && typeof target === 'object') {
            target = (target as Record<string, unknown>)[seg];
          }
        }
        const lastSeg = segments[segments.length - 1];
        if (Array.isArray(target)) {
          (target as unknown[])[Number(lastSeg)] = newValue;
        } else if (target && typeof target === 'object') {
          (target as Record<string, unknown>)[lastSeg] = newValue;
        }
        onEdit(JSON.stringify(clone, null, 2));
      } catch {
        // If something goes wrong, silently fail
      }
    }, [effectiveInput, onEdit]);

    // Handle adding new properties or array items
    const handleNodeAdd = React.useCallback((path: string, kind: 'property' | 'item', key?: string) => {
      if (!onEdit) return;
      try {
        const clone = parseJsonLenient(effectiveInput);
        const segments = path.split('.').slice(1); // drop "root"
        let target: unknown = clone;
        for (const seg of segments) {
          if (Array.isArray(target)) {
            target = (target as unknown[])[Number(seg)];
          } else if (target && typeof target === 'object') {
            target = (target as Record<string, unknown>)[seg];
          }
        }
        if (kind === 'property' && key && target && typeof target === 'object' && !Array.isArray(target)) {
          (target as Record<string, unknown>)[key] = '';
        } else if (kind === 'item' && Array.isArray(target)) {
          target.push('');
        }
        onEdit(JSON.stringify(clone, null, 2));
      } catch {
        // silently fail
      }
    }, [effectiveInput, onEdit]);

    // Handle deleting a property or array item
    const handleNodeDelete = React.useCallback((path: string) => {
      if (!onEdit) return;
      try {
        const clone = parseJsonLenient(effectiveInput);
        const segments = path.split('.').slice(1); // drop "root"
        if (segments.length === 0) return; // can't delete root
        const parentSegments = segments.slice(0, -1);
        const lastSeg = segments[segments.length - 1];
        let parent: unknown = clone;
        for (const seg of parentSegments) {
          if (Array.isArray(parent)) {
            parent = (parent as unknown[])[Number(seg)];
          } else if (parent && typeof parent === 'object') {
            parent = (parent as Record<string, unknown>)[seg];
          }
        }
        if (Array.isArray(parent)) {
          parent.splice(Number(lastSeg), 1);
        } else if (parent && typeof parent === 'object') {
          delete (parent as Record<string, unknown>)[lastSeg];
        }
        onEdit(JSON.stringify(clone, null, 2));
      } catch {
        // silently fail
      }
    }, [effectiveInput, onEdit]);
    
    if (parsedData && typeof parsedData === 'object' && '__error' in parsedData) {
      const errorMsg = (parsedData as { __error: string }).__error;
      return (
        <div className="p-4 text-red-500">
          <div className="font-semibold">Parse Error</div>
          <div className="text-sm mt-1">{errorMsg}</div>
          <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-32">
            {input.substring(0, 200)}...
          </pre>
        </div>
      );
    }
    
    return (
      <JsonTreeView
        data={parsedData}
        initialExpanded={config.initialExpanded as boolean ?? true}
        maxDepth={config.maxDepth as number ?? 10}
        // Search/filter state is fully uncontrolled — owned by JsonTreeView internally.
        // Do NOT pass searchTerm/searchMode/showTypes as controlled props, or the
        // nullish coalescing in JsonTreeView will override internal state.
        // See: architectural-invariants.md → Visualizer Isolation
        zeroIndexed={config.zeroIndexed as boolean ?? true}
        chunkSize={config.chunkSize as number ?? 100}
        maxTraversalNodes={config.maxTraversalNodes as number ?? 5000}
        editMode={editMode}
        onValueEdit={handleValueEdit}
        onNodeAdd={handleNodeAdd}
        onNodeDelete={handleNodeDelete}
        expandAllTrigger={config._expandAllTrigger as number | undefined}
        collapseAllTrigger={config._collapseAllTrigger as number | undefined}
      />
    );
  }
  
  /**
   * Convert visualization back to text (formatted JSON).
   */
  toText(input: string, _config: Record<string, unknown>): string {
    return input;
  }
}

export const JsonTreeVisualizerTool = new JsonTreeVisualizerToolImpl();

